import { useEffect, useState } from 'react';
import { BrowserRouter, Link, Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { isTenantSuspended } from '@kilat/shared';
import { Skeleton } from '@kilat/ui';
import { AuthProvider, useAuth } from './lib/use-auth.js';
import { PlatformBrandProvider } from './lib/use-platform-brand.js';
import { I18nProvider, useI18n } from './lib/use-i18n.js';
import { ThemeProvider } from '@kilat/ui';
import { ActiveOutletProvider } from './lib/use-active-outlet.js';
import { tenantsRepo } from './lib/repositories/tenants-repo.js';
import { logger } from './lib/logger.js';
import { WelcomePage } from './routes/welcome.js';
import { QueuePage } from './routes/queue.js';
import { PosPage } from './routes/pos.js';
import { QrPrintPage } from './routes/qr-print.js';
import { VouchersPage } from './routes/vouchers.js';
import { OutletsPage } from './routes/outlets.js';
import { CustomersPage } from './routes/customers.js';
import { ReviewsPage } from './routes/reviews.js';
import { TagsPage } from './routes/tags.js';
import { SuperadminAgentsPage } from './routes/superadmin-agents.js';
import { SuperadminAiPage } from './routes/superadmin-ai.js';
import { SuperadminPaymentsPage } from './routes/superadmin-payments.js';
import { SuperadminPayoutsPage } from './routes/superadmin-payouts.js';
import { SuperadminPlansPage } from './routes/superadmin-plans.js';
import { SuperadminIntegrationsPage } from './routes/superadmin-integrations.js';
import { SuperadminSetupPage } from './routes/superadmin-setup.js';
import { SuperadminRecruitersPage } from './routes/superadmin-recruiters.js';
import { SuperadminTenantsPage } from './routes/superadmin-tenants.js';
import { SuperadminDirectoryPage } from './routes/superadmin-directory.js';
import { SuperadminFinancePage } from './routes/superadmin-finance.js';
import { SuperadminMapPage } from './routes/superadmin-map.js';
import { MapPage } from './routes/map.js';
import { BrandingHoursPage } from './routes/branding-hours.js';
import { BillingPage } from './routes/billing.js';
import { RefundsPage } from './routes/refunds.js';
import { CatalogPage } from './routes/catalog.js';
import { ProductsPage } from './routes/products.js';
import { MarketingPage } from './routes/marketing.js';
import { MarketingSegmentsPage } from './routes/marketing-segments.js';
import { MarketingJourneysPage } from './routes/marketing-journeys.js';
import { ShareKitPage } from './routes/marketing-share.js';
import { AcademyPage } from './routes/academy.js';
import { ReportsPage } from './routes/reports.js';
import { StaffPage } from './routes/staff.js';
import { LoyaltyPromotionsPage } from './routes/loyalty-promotions.js';
import { LoyaltyPackagesPage } from './routes/loyalty-packages.js';
import { LoyaltySettingsPage } from './routes/loyalty-settings.js';
import { LoyaltyChallengesPage } from './routes/loyalty-challenges.js';
import { LoyaltyAchievementsPage } from './routes/loyalty-achievements.js';
import { LoyaltySpinWheelPage } from './routes/loyalty-spin-wheel.js';
import { InboxPage } from './routes/inbox.js';
import { DashboardPushProvider, DashboardPushToast } from './lib/push/dashboard-push.js';
import { PwaUpdateBanner } from './components/pwa-update-banner.js';
import { AppShellBar } from './components/app-shell-bar.js';
import { PastDueBanner } from './components/past-due-banner.js';
import { TrialBanner } from './components/trial-banner.js';
import { SignInScreen, NoOperatorAccount } from './components/sign-in-screen.js';

/**
 * Dashboard shell.
 *
 * Provider order: PlatformBrand → Auth → ActiveOutlet → Router.
 *
 * HomeRoute (`*`) routes by onboarding state:
 *   - not signed in → SignInScreen (returning-user login; replaced the old
 *     dead-end SignedOut placeholder)
 *   - onboardingCompleted !== true → /welcome (wizard)
 *   - onboardingCompleted === true → /queue
 */
export function App() {
  return (
    <PlatformBrandProvider fallback={<Splash />}>
      <I18nProvider>
        <ThemeProvider>
          <AuthProvider>
            <TenantScopedProviders>
              <BrowserRouter>
                {/* Push: inside the router so a tapped notification routes in-app. */}
                <DashboardPushProvider>
                  {/* In-flow amber warning at the very top while past_due — pushes
                    route content down (no fixed bar → no header overlap). It sits
                    above the fixed-corner PwaUpdateBanner/AskKilat chrome. */}
                  {/* One shell for the whole product. 36 of 37 screens rendered no
                    navigation at all before this — you arrived and the only exit
                    was a "Back to queue" link, if the screen bothered to have
                    one. Mounted here so a new screen inherits nav by existing. */}
                  <AppShellBar />
                  <PastDueBanner />
                  {/* In-flow CALM strip while on a free trial (roadmap #11) — same
                    in-flow slot as PastDueBanner; the two never render together
                    (`trial` and `past_due` are mutually-exclusive TenantStatus
                    values), so no stacking to coordinate. */}
                  <TrialBanner />
                  <Routes>
                    {/* Every screen but home is reached by a direct link too — a tapped
                      notification, a bookmark, a link a colleague sent. Those used to
                      skip the sign-in check that only home ran, and showed "we couldn't
                      find your account" with a Reload that could never help. */}
                    <Route element={<SignedInOnly />}>
                      <Route path="/welcome" element={<WelcomePage />} />
                      <Route path="/queue" element={<QueuePage />} />
                      <Route path="/pos" element={<PosPage />} />
                      <Route path="/qr-print" element={<QrPrintPage />} />
                      <Route path="/outlets" element={<OutletsPage />} />
                      <Route path="/customers" element={<CustomersPage />} />
                      <Route path="/reviews" element={<ReviewsPage />} />
                      <Route path="/loyalty/settings" element={<LoyaltySettingsPage />} />
                      <Route path="/loyalty/challenges" element={<LoyaltyChallengesPage />} />
                      <Route path="/loyalty/achievements" element={<LoyaltyAchievementsPage />} />
                      <Route path="/loyalty/spin-wheel" element={<LoyaltySpinWheelPage />} />
                      <Route path="/loyalty/vouchers" element={<VouchersPage />} />
                      <Route path="/loyalty/tags" element={<TagsPage />} />
                      <Route path="/loyalty/promotions" element={<LoyaltyPromotionsPage />} />
                      <Route path="/loyalty/packages" element={<LoyaltyPackagesPage />} />
                      <Route path="/branding/hours" element={<BrandingHoursPage />} />
                      <Route path="/billing" element={<BillingPage />} />
                      <Route path="/refunds" element={<RefundsPage />} />
                      <Route path="/catalog" element={<CatalogPage />} />
                      <Route path="/products" element={<ProductsPage />} />
                      <Route path="/reports" element={<ReportsPage />} />
                      <Route path="/academy" element={<AcademyPage />} />
                      <Route path="/map" element={<MapPage />} />
                      <Route path="/staff" element={<StaffPage />} />
                      <Route path="/marketing" element={<MarketingPage />} />
                      <Route path="/marketing/segments" element={<MarketingSegmentsPage />} />
                      <Route path="/inbox" element={<InboxPage />} />
                      <Route path="/marketing/journeys" element={<MarketingJourneysPage />} />
                      <Route path="/marketing/share" element={<ShareKitPage />} />
                      <Route path="/superadmin/agents" element={<SuperadminAgentsPage />} />
                      <Route path="/superadmin/ai" element={<SuperadminAiPage />} />
                      <Route path="/superadmin/payments" element={<SuperadminPaymentsPage />} />
                      <Route path="/superadmin/payouts" element={<SuperadminPayoutsPage />} />
                      <Route path="/superadmin/plans" element={<SuperadminPlansPage />} />
                      <Route
                        path="/superadmin/integrations"
                        element={<SuperadminIntegrationsPage />}
                      />
                      <Route path="/superadmin/setup" element={<SuperadminSetupPage />} />
                      <Route path="/superadmin/recruiters" element={<SuperadminRecruitersPage />} />
                      <Route path="/superadmin/tenants" element={<SuperadminTenantsPage />} />
                      <Route path="/superadmin/directory" element={<SuperadminDirectoryPage />} />
                      <Route path="/superadmin/finance" element={<SuperadminFinancePage />} />
                      <Route path="/superadmin/map" element={<SuperadminMapPage />} />
                    </Route>
                    <Route path="*" element={<HomeRoute />} />
                  </Routes>
                  <PwaUpdateBanner />
                  <DashboardPushToast />
                </DashboardPushProvider>
              </BrowserRouter>
            </TenantScopedProviders>
          </AuthProvider>
        </ThemeProvider>
      </I18nProvider>
    </PlatformBrandProvider>
  );
}

function TenantScopedProviders({ children }: { readonly children: React.ReactNode }) {
  const { claims } = useAuth();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;
  return <ActiveOutletProvider tenantId={tenantId}>{children}</ActiveOutletProvider>;
}

/**
 * Gate for every route except home (which routes by onboarding state itself).
 * A signed-out visitor gets the sign-in screen AT the URL they asked for, so
 * signing in lands them on that screen rather than home.
 */
function SignedInOnly() {
  const { t } = useI18n();
  const { user, claims, loading } = useAuth();
  if (loading) return <Splash label={t('auth.splash.signingIn')} />;
  if (!user) return <SignInScreen />;
  // Claims arrive a moment after the user: without this wait every screen
  // briefly decides the person has no account.
  if (claims === null) return <Splash label={t('auth.splash.loadingAccount')} />;
  return <Outlet />;
}

/** True when the token carries a super-admin grant for at least one brand (ADR-0017). */
function hasSuperadminClaim(claims: Record<string, unknown> | null): boolean {
  const s = claims?.['superadmin'];
  if (typeof s !== 'object' || s === null) return false;
  return Object.values(s).some((v) => v === true);
}

function HomeRoute() {
  const { t } = useI18n();
  const { user, claims, loading } = useAuth();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;
  const [tenantView, setTenantView] = useState<
    { kind: 'unknown' } | { kind: 'ready'; suspended: boolean; onboardingCompleted: boolean }
  >({ kind: 'unknown' });

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const t = await tenantsRepo.get(tenantId);
        if (cancelled) return;
        setTenantView({
          kind: 'ready',
          suspended: t ? isTenantSuspended(t.profile.status) : false,
          onboardingCompleted: t?.profile.onboardingCompleted === true,
        });
      } catch (e) {
        logger.warn('HomeRoute: tenant load failed', { error: String(e) });
        // On a transient read failure show the wizard rather than either
        // locking the operator out OR falsely showing the suspended screen.
        if (!cancelled)
          setTenantView({ kind: 'ready', suspended: false, onboardingCompleted: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (loading) return <Splash label={t('auth.splash.signingIn')} />;
  if (!user) return <SignInScreen />;
  // A signed-in super-admin with no operator tenant belongs in the super-admin
  // area — without this HomeRoute would spin forever on "Loading your account…".
  if (!tenantId && hasSuperadminClaim(claims)) return <Navigate to="/superadmin/tenants" replace />;
  if (!tenantId) {
    // `claims === null` = the ID-token claims are still resolving (show the
    // splash); a non-null claims set with no tenant means they authenticated
    // but have no operator account → a next-action screen, never a dead-end.
    if (claims === null) return <Splash label={t('auth.splash.loadingAccount')} />;
    return <NoOperatorAccount />;
  }
  if (tenantView.kind === 'unknown') return <Splash label={t('auth.splash.loading')} />;
  // Suspended tenants get a pay-to-reactivate lock instead of the queue.
  // /billing stays reachable (it's a distinct route, not routed through here),
  // so this is a next-action screen, not a dead-end.
  if (tenantView.suspended) return <SuspendedLock />;
  return <Navigate to={tenantView.onboardingCompleted ? '/queue' : '/welcome'} replace />;
}

/**
 * Shown on the dashboard home when the tenant is `suspended` for non-payment.
 * Tells the operator exactly what happened and the single next action — pay
 * the outstanding invoice on /billing to reactivate ([[feedback-no-dead-ends]]).
 * /billing itself is NOT locked, so the pay path stays open.
 */
function SuspendedLock() {
  const { t } = useI18n();
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <div style={{ maxWidth: 460 }}>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            fontWeight: 600,
            letterSpacing: 1,
            textTransform: 'uppercase',
            color: 'var(--color-tone-error, #b91c1c)',
          }}
        >
          {t('dashboard.suspended.eyebrow')}
        </p>
        <h1 style={{ fontSize: 'var(--text-h2)', fontWeight: 600, marginTop: 'var(--space-2)' }}>
          {t('dashboard.suspended.title')}
        </h1>
        <p
          style={{
            fontSize: 'var(--text-body)',
            color: 'var(--color-on-surface-muted)',
            marginTop: 'var(--space-3)',
          }}
        >
          {t('dashboard.suspended.body')}
        </p>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            marginTop: 'var(--space-3)',
          }}
        >
          {t('dashboard.suspended.note')}
        </p>
        <Link
          to="/billing"
          style={{
            display: 'inline-block',
            marginTop: 'var(--space-6)',
            background: 'var(--color-primary)',
            color: 'var(--color-primary-fg)',
            padding: '14px 24px',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-body)',
            fontWeight: 500,
            textDecoration: 'none',
            boxShadow: 'var(--shadow-2)',
          }}
        >
          {t('dashboard.suspended.cta')}
        </Link>
      </div>
    </main>
  );
}

/**
 * Branded boot/route splash — the most-seen surface in the dashboard, so it
 * carries the Kilat gradient logo mark + a shimmer pill instead of bare
 * "Loading…" text. The shimmer is the shared `@kilat/ui` `Skeleton` (auto-
 * neutralised under `prefers-reduced-motion` by premium-motion.css).
 *
 * `label` is OPTIONAL and screen-reader-only: the visual conveys "loading"
 * via the shimmer, while the label (when a caller has i18n available) gives
 * assistive tech the specific status. The PlatformBrand fallback renders
 * BEFORE the I18n provider, so it passes no label — hence the optionality.
 */
function Splash({ label }: { readonly label?: string }) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        style={{ display: 'grid', gap: 'var(--space-4)', justifyItems: 'center' }}
      >
        <span
          aria-hidden
          style={{
            width: 48,
            height: 48,
            borderRadius: 16,
            background: 'linear-gradient(135deg, var(--color-primary, #0d7a4f), #0f8659)',
            boxShadow: 'var(--shadow-2, 0 8px 20px rgba(13,122,79,0.28))',
          }}
        />
        <Skeleton width={140} height={12} radius={999} />
        {label ? <span style={splashSrOnly}>{label}</span> : null}
      </div>
    </main>
  );
}

const splashSrOnly: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};
